import type { QueryClient } from '@tanstack/react-query';

/**
 * Canonical set of React Query keys whose data reflects the user's points
 * balance / wallet. Every client-side flow that mints, claims, or moves points
 * MUST invalidate this set so the top-bar balance (`useWallet` -> `['wallet']`)
 * and every other balance-derived surface refetch immediately.
 *
 * Keys are intentionally un-suffixed (no user id) because React Query's default
 * prefix matching also invalidates user-scoped variants such as
 * `['profile', userId]`, `['point-transactions', userId, limit]`, and
 * `['weekly-earning-stats', userId]`.
 *
 * REK-1796: introduced after a QA bug where answering a quick-win survey awarded
 * points but left the top-bar balance stale because the flow never invalidated
 * `['wallet']`. Centralizing the set here keeps the invariant from drifting per
 * call site again.
 *
 */
export const BALANCE_QUERY_KEYS: readonly (readonly string[])[] = [
  ['wallet'],
  ['dashboard-stats'],
  ['dashboard-bundle'],
  ['dashboard-data'],
  ['profile'],
  ['user-profile'],
  ['point-transactions'],
  ['weekly-earning-stats'],
  ['user-entries'],
];

/**
 * Invalidate every balance-affecting query so all surfaces that display the
 * user's points balance refetch. Call this from the success path of any
 * client-side mutation that changes the user's points.
 */
export function invalidateBalanceQueries(queryClient: QueryClient): void {
  for (const queryKey of BALANCE_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey });
  }
}
